"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type AddQuoteResult,
  type CatEntryPage,
  type CatQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { CatBreedAutoComplete } from "@/components/shared/catBreedAutoComplete/CatBreedAutoComplete";
import { ComposedInputField } from "@/components/shared/composedInputField/ComposedInputField";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import {
  type CatEntryFormType,
  CatEntryValidationSchema,
} from "@/validators/products/cat/CatEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: CatEntryPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: CatQuote) => Promise<AddQuoteResult>;
};

export const CatEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content: catEntryPage,
    isLoading,
    submitHandler,
  } = useEntry<CatQuote, CatEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "catEntryPage",
  });

  const defaultValues: CatQuote = {
    productType: ProductType.CAT,
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    catCovered: {
      catName: "",
      catBreed: "",
    },
  };
  const { handleSubmit, control, formState } = useForm<CatEntryFormType>({
    defaultValues,
    resolver: yupResolver(CatEntryValidationSchema(catEntryPage)),
    disabled: isLoading,
  });
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="catCovered.catName"
            render={({ field }) => (
              <ComposedInputField
                id={field.name}
                placeholder={
                  catEntryPage?.catNameQuestion?.placeholder?.value ?? undefined
                }
                data-testid="cat-name-input"
                errorMessage={errors?.catCovered?.catName?.message}
                popoverMessage={catEntryPage?.catNameQuestion?.popover?.value}
                {...field}
              >
                {catEntryPage?.catNameQuestion?.label?.value}
              </ComposedInputField>
            )}
          />
          <Controller
            control={control}
            name="catCovered.catBreed"
            render={({ field }) => (
              <CatBreedAutoComplete
                id="destinationCountry"
                dataTestid="cat-breed-input"
                errorMessage={errors.catCovered?.catBreed?.message}
                placeholder={
                  catEntryPage?.catBreedQuestion?.placeholder?.value ??
                  undefined
                }
                popoverMessage={catEntryPage?.catBreedQuestion?.popover?.value}
                {...field}
              >
                {catEntryPage?.catBreedQuestion?.label?.value}
              </CatBreedAutoComplete>
            )}
          />
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                data-testid="policy-holder-ssn-input"
                placeholder={
                  catEntryPage?.policyHolderSsn?.placeholder?.value ?? undefined
                }
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                popoverMessage={catEntryPage?.policyHolderSsn?.popover?.value}
                errorMessage={errors?.policyHolderSsn?.message}
                {...field}
              >
                {catEntryPage?.policyHolderSsn?.label?.value}
              </SsnInput>
            )}
          />
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="cat-entry-submit-button"
            loading={isLoading}
          >
            {catEntryPage?.submitButton?.label?.value}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
